import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth, formatApiError, api } from '@/App';
import { EnvelopeSimple, Lock, User, Phone, Buildings, IdentificationCard, MapPin } from '@phosphor-icons/react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_8ddecf7f-a4c9-427e-b02f-3053db4df717/artifacts/217o5tly_Marema_Logo.jpg";

const PortalRegisterPage = () => {
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [form, setForm] = useState({
    account_type: 'business',
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    company_name: '',
    id_number: '',
    residential_address: '',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (form.password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setIsLoading(true);
    try {
      await api.post('/portal/register', {
        account_type: form.account_type,
        full_name: form.full_name,
        email: form.email,
        phone: form.phone,
        password: form.password,
        company_name: form.company_name,
        id_number: form.id_number,
        residential_address: form.residential_address,
      });
      toast.success('Registration successful! Welcome to Marema Consulting.');
      await checkAuth();
      navigate('/portal');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const set = (key, val) => setForm(prev => ({ ...prev, [key]: val }));

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Marema Consulting" className="h-16 w-auto" />
          </Link>
        </div>
      </header>

      <div className="relative h-[160px] bg-cover bg-center" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80')` }}>
        <div className="absolute inset-0 bg-[#4CAF50]/80"></div>
        <div className="relative z-10 h-full flex items-center justify-center">
          <h1 className="text-4xl font-light text-white">Create Your Account</h1>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-6 py-12">
        <div className="bg-white p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="register-form">
            {/* Account Type */}
            <div>
              <Label className="mb-3 block">Account Type *</Label>
              <div className="flex gap-4">
                <button type="button" onClick={() => set('account_type', 'business')}
                  className={`flex-1 p-4 border-2 text-center transition-colors ${form.account_type === 'business' ? 'border-[#4CAF50] bg-[#4CAF50]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="type-business">
                  <Buildings className="w-8 h-8 mx-auto mb-2 text-[#4CAF50]" />
                  <p className="font-semibold text-sm">Business</p>
                </button>
                <button type="button" onClick={() => set('account_type', 'personal')}
                  className={`flex-1 p-4 border-2 text-center transition-colors ${form.account_type === 'personal' ? 'border-[#4CAF50] bg-[#4CAF50]/5' : 'border-gray-200 hover:border-gray-300'}`}
                  data-testid="type-personal">
                  <User className="w-8 h-8 mx-auto mb-2 text-[#4CAF50]" />
                  <p className="font-semibold text-sm">Personal</p>
                </button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name *</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={form.full_name} onChange={(e) => set('full_name', e.target.value)} required placeholder="John Doe" className="pl-10 rounded-none" data-testid="reg-name" />
                </div>
              </div>
              <div>
                <Label>Email Address *</Label>
                <div className="relative">
                  <EnvelopeSimple className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} required placeholder="you@example.com" className="pl-10 rounded-none" data-testid="reg-email" />
                </div>
              </div>
            </div>

            <div>
              <Label>Phone Number</Label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} placeholder="+27 XX XXX XXXX" className="pl-10 rounded-none" data-testid="reg-phone" />
              </div>
            </div>

            {/* Business fields */}
            {form.account_type === 'business' && (
              <div>
                <Label>Company Name *</Label>
                <div className="relative">
                  <Buildings className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input value={form.company_name} onChange={(e) => set('company_name', e.target.value)} required placeholder="Company (Pty) Ltd" className="pl-10 rounded-none" data-testid="reg-company" />
                </div>
              </div>
            )}

            {/* Personal fields */}
            {form.account_type === 'personal' && (
              <>
                <div>
                  <Label>ID Number</Label>
                  <div className="relative">
                    <IdentificationCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input value={form.id_number} onChange={(e) => set('id_number', e.target.value)} placeholder="SA ID Number" className="pl-10 rounded-none" data-testid="reg-id" />
                  </div>
                </div>
                <div>
                  <Label>Residential Address</Label>
                  <div className="relative">
                    <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input value={form.residential_address} onChange={(e) => set('residential_address', e.target.value)} placeholder="Street address, city" className="pl-10 rounded-none" data-testid="reg-address" />
                  </div>
                </div>
              </>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="password" value={form.password} onChange={(e) => set('password', e.target.value)} required placeholder="Min 8 characters" className="pl-10 rounded-none" data-testid="reg-password" />
                </div>
              </div>
              <div>
                <Label>Confirm Password *</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input type="password" value={form.confirm_password} onChange={(e) => set('confirm_password', e.target.value)} required placeholder="Repeat password" className="pl-10 rounded-none" data-testid="reg-confirm" />
                </div>
              </div>
            </div>

            <Button type="submit" disabled={isLoading} className="w-full bg-[#4CAF50] hover:bg-[#45a049] text-white rounded-none py-3 font-medium" data-testid="reg-submit">
              {isLoading ? 'Creating Account...' : 'Create Account'}
            </Button>

            <p className="text-center text-sm text-gray-600">
              Already have an account? <Link to="/portal/login" className="text-[#4CAF50] hover:underline font-medium">Log in</Link>
            </p>

            <p className="text-xs text-gray-400 text-center">Service turnaround time: 30-90 days depending on service type and municipality.</p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PortalRegisterPage;
